<template>
  <form class="pb-40" @submit.prevent="onSubmit">
    <div class="grid-cols-9 gap-12 mt-4 md:mt-12 md:grid">
      <div class="flex col-span-3 md:justify-end">
        <p class="font-semibold uppercase text-brand-orange text-tiny">
          Vaše údaje
        </p>
      </div>
      <div class="col-span-6 mt-8 md:mt-0">
        <div class="w-full md:w-[400px] space-y-6">
          <form-input
            :errors="getErrorsFor('firstName')"
            required
            label="Jméno"
            v-model="v$.form.firstName.$model"
          ></form-input>
          <form-input
            :errors="getErrorsFor('lastName')"
            required
            label="Příjmení"
            v-model="v$.form.lastName.$model"
          ></form-input>
          <form-input
            required
            @input="delayTouch(v$.form.email)"
            :errors="getErrorsFor('email')"
            label="E-mailová adresa"
            type="email"
            v-model="v$.form.email.$model"
          ></form-input>
          <form-input
            :secondaryLabel="'Na visačku'"
            required
            :errors="getErrorsFor('company')"
            label="Název e-shopu"
            v-model="v$.form.company.$model"
          ></form-input>
          <form-input
            required
            :errors="getErrorsFor('web')"
            label="URL e-shopu"
            v-model="v$.form.web.$model"
          ></form-input>
          <form-input
            :errors="getErrorsFor('ic')"
            label="IČ"
            required
            v-model="v$.form.ic.$model"
          ></form-input>

          <RelationshipDropdown
            :errors="getErrorsFor('relationship')"
            v-model="v$.form.relationship.$model"
          />

          <turnover-dropdown
            :errors="getErrorsFor('turnover')"
            v-model="v$.form.turnover.$model"
          ></turnover-dropdown>

          <EmployeesCountDropdown
            :errors="getErrorsFor('employeesCount')"
            v-model="v$.form.employeesCount.$model"
          ></EmployeesCountDropdown>
        </div>
        <p class="mt-6 mb-4 font-medium">
          Které oblasti nyní v e-shopu prioritně řešíte
        </p>
        <div
          v-for="(position, i) in positions"
          :key="i"
          class="relative flex items-center mt-3"
        >
          <div class="flex items-center h-5">
            <input
              v-model="form.positions"
              :id="'position_' + position.id"
              type="checkbox"
              :value="position.id"
              class="w-5 h-5 border border-gray-300 rounded-md form-checkbox text-brand-blue focus:ring-4 focus:ring-opacity-25 focus:ring-brand-blue"
            />
          </div>
          <div class="ml-3 text-sm">
            <label
              :for="'position_' + position.id"
              class="font-medium text-brand-gray-900"
              >{{ position.name }}</label
            >
          </div>
        </div>

        <transition
          enter-active-class="transition duration-300 ease-out transform"
          enter-from-class="-translate-y-3 opacity-0"
          enter-to-class="translate-y-0 opacity-100"
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
           <div v-if="errorMessage !== ''" class="p-4 mt-8 lg:max-w-[400px] rounded-md bg-brand-red">
          <div class="flex items-center">
            <div class="shrink-0">
              <ExclamationIcon class="w-5 h-5 text-white" aria-hidden="true" />
            </div>
            <div class="ml-3">
              <p class="text-sm font-semibold text-white text-body">
                {{ errorMessage }}
              </p>
            </div>
            <div class="pl-3 ml-auto">
              <div class="-mx-1.5 -my-1.5">
                <button
                  @click="errorMessage = ''"
                  type="button"
                  class="inline-flex bg-white rounded-md p-1.5 text-brand-red focus:outline-none"
                >
                  <span class="sr-only">Dismiss</span>
                  <XIcon class="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
        </transition>

        <transition
          enter-active-class="transition duration-300 ease-out transform"
          enter-from-class="-translate-y-3 opacity-0"
          enter-to-class="translate-y-0 opacity-100"
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="showSuccessMessage"
            class="p-4 mt-12 rounded-md bg-brand-green"
          >
            <div class="flex items-center">
              <div class="shrink-0">
                <CheckCircleIcon
                  class="w-5 h-5 text-white"
                  aria-hidden="true"
                />
              </div>
              <div class="ml-3">
                <p class="text-sm font-semibold text-white text-body">
                  Úspěšně jste se zapsal/a jako náhradník.
                </p>
              </div>
            </div>
          </div>
        </transition>

        <div
          class="mt-6 lg:max-w-[400px]"
        >
          <button
            :disabled="showSuccessMessage"
            :class="{
              'opacity-60 cursor-not-allowed pointer-events-none':
                showSuccessMessage,
            }"
            class="flex items-center justify-center w-full px-8 py-4 text-white rounded-md group hover:opacity-80 md:justify-start md:w-auto text-button bg-brand-blue"
            type="submit"
          >
            <span v-if="!isAlternate">Dokončit registraci</span>
            <span v-else>Zapsat se jako náhradník</span>
            <svg
              class="ml-4 transition-all duration-200 transform group-hover:translate-x-1"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.2421 5.99219C17.0929 5.99222 16.947 6.03679 16.8232 6.12019C16.6994 6.20358 16.6033 6.32202 16.5472 6.46033C16.4911 6.59865 16.4776 6.75056 16.5083 6.89662C16.539 7.04269 16.6126 7.17627 16.7197 7.28027L20.4394 11H1.74994C1.65056 10.9986 1.55188 11.017 1.45966 11.054C1.36743 11.0911 1.28349 11.1461 1.21271 11.2159C1.14194 11.2857 1.08573 11.3688 1.04737 11.4605C1.00901 11.5522 0.989258 11.6506 0.989258 11.75C0.989258 11.8494 1.00901 11.9478 1.04737 12.0395C1.08573 12.1312 1.14194 12.2143 1.21271 12.2841C1.28349 12.3539 1.36743 12.4089 1.45966 12.446C1.55188 12.483 1.65056 12.5014 1.74994 12.5H20.4394L16.7197 16.2197C16.6477 16.2888 16.5902 16.3716 16.5506 16.4632C16.511 16.5548 16.4901 16.6534 16.4891 16.7532C16.4881 16.8529 16.507 16.9519 16.5447 17.0443C16.5824 17.1367 16.6382 17.2206 16.7088 17.2912C16.7793 17.3617 16.8633 17.4175 16.9556 17.4552C17.048 17.4929 17.147 17.5118 17.2468 17.5108C17.3466 17.5098 17.4451 17.4889 17.5367 17.4493C17.6283 17.4097 17.7111 17.3523 17.7802 17.2803L22.7802 12.2803C22.9208 12.1396 22.9998 11.9489 22.9998 11.75C22.9998 11.5511 22.9208 11.3604 22.7802 11.2197L17.7802 6.21973C17.7103 6.14773 17.6267 6.0905 17.5342 6.05142C17.4418 6.01233 17.3425 5.99219 17.2421 5.99219Z"
                fill="white"
              />
            </svg>
          </button>


          <p class="mt-4 text-tiny text-brand-gray-500">Beru na vědomí zpracování osobních údajů. Více o tom, jak pracujeme s vašimi daty <a class="text-brand-blue underline" :href="route('frontend.gdpr')" target="_blank">zde</a>.</p>
        </div>

      </div>
    </div>
  </form>
</template>

<script>
import { computed, ref, nextTick } from "vue";

import FormInput from "../Atomic/Input.vue";

import useVuelidate from "@vuelidate/core";
import { required, email, helpers } from "@vuelidate/validators";

import { CheckCircleIcon, XIcon, ExclamationIcon } from "@heroicons/vue/solid";

import axios from "axios";

import TurnoverDropdown from "./TurnoverDropdown.vue";
import EmployeesCountDropdown from "./EmployeesCountDropdown.vue";
import RelationshipDropdown from "./RelationshipDropdown.vue";

import { useReCaptcha } from "vue-recaptcha-v3";

const requiredTranslation = "Toto pole je vyžádováno";
const emailFormatTranslation = "Nesprávný formát e-mailové adresy";

const positionsData = [
  {
    id: "ppc",
    name: "PPC",
  },
  {
    id: "porovnavac",
    name: "Zbožové porovnávače",
  },
  {
    id: "logistika",
    name: "Logistika",
  },
  {
    id: "seo",
    name: "SEO",
  },
  {
    id: "emailing",
    name: "Emailing",
  },
  {
    id: "analytics",
    name: "Analytika a e-commerce data",
  },
  {
    id: "support",
    name: "Zákaznická podpora",
  },
  {
    id: "expansion",
    name: "Zahraniční expanze",
  },
  {
    id: "ux-a-cro",
    name: "UX & CRO",
  },
  {
    id: "strategy",
    name: "Strategické řízení",
  },
  {
    id: "ecommerce",
    name: "Rozvoj a vývoj e-commerce platformy",
  },
  {
    id: "finance",
    name: "Finanční řízení",
  },
  {
    id: "legals",
    name: "Právo a další legislativa",
  },
  {
    id: "tech",
    name: "Hosting, platební brány a další technologie",
  },
];

export default {
  components: {
    FormInput,
    CheckCircleIcon,
    XIcon,
    ExclamationIcon,
    TurnoverDropdown,
    EmployeesCountDropdown,
    RelationshipDropdown,
  },
  validations() {
    return {
      form: {
        firstName: {
          required: helpers.withMessage(requiredTranslation, required),
          $lazy: true,
        },
        lastName: {
          required: helpers.withMessage(requiredTranslation, required),
          $lazy: true,
        },
        email: {
          $lazy: true,
          email: helpers.withMessage(emailFormatTranslation, email),
          required: helpers.withMessage(requiredTranslation, required),
        },
        company: {
          $lazy: true,
          required: helpers.withMessage(requiredTranslation, required),
        },
        web: {
          $lazy: true,
          required: helpers.withMessage(requiredTranslation, required),
        },
        ic: {
          $lazy: true,
          required: helpers.withMessage(requiredTranslation, required),
        },
        turnover: {
          $lazy: true,
          required: helpers.withMessage(requiredTranslation, required),
        },
        employeesCount: {
          $lazy: true,
          required: helpers.withMessage(requiredTranslation, required),
        },
        relationship: {
          $lazy: true,
          required: helpers.withMessage(requiredTranslation, required),
        },
      },
    };
  },

  props: {
    isAlternate: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

    const v$ = useVuelidate({ $lazy: true });
    const touchMap = new WeakMap();

    const positions = ref(positionsData);

    const showSuccessMessage = ref(false);
    const errorMessage = ref('');

    const form = ref({
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      web: "",
      ic: "",
      turnover: undefined,
      employeesCount: undefined,
      relationship: undefined,
      positions: [],
    });

    const getErrorsFor = computed(() => (fieldName) => {
      if (v$.value.form[fieldName]?.$errors[0]?.$message) {
        return [v$.value.form[fieldName].$errors[0].$message];
      }

      return [];
    });

    const delayTouch = (v$) => {
      v$.$reset();
      if (touchMap.has(v$)) {
        clearTimeout(touchMap.get(v$));
      }
      touchMap.set(v$, setTimeout(v$.$touch, 1000));
    };

    const onSubmit = async () => {
      v$.value.$touch();
      v$.value.$validate();

      if (v$.value.$error) {
        nextTick(() => {
          let domRect = document
            .querySelector(".error")
            .getBoundingClientRect();
          window.scrollTo(
            domRect.left + document.documentElement.scrollLeft,
            domRect.top + document.documentElement.scrollTop - 180
          );
        });
        return;
      }

      await recaptchaLoaded();
      const token = await executeRecaptcha();

      const data = {
        first_name: form.value.firstName,
        last_name: form.value.lastName,
        email: form.value.email,
        company: form.value.company,
        questionnaire: JSON.stringify({
          web: form.value.web,
          ic: form.value.ic,
          turnover: form.value.turnover.id,
          employeesCount: form.value.employeesCount.id,
          positions: form.value.positions,
          relationship: form.value.relationship.id,
        }),
        recaptcha_token: token,
      };

      try {
        if (props.isAlternate) {
          const res = await axios.post(route("user.register-applicant"), data);

          if (res.status === 200) {
            showSuccessMessage.value = true;
          }

        } else {
          const res = await axios.post(route("user.register-web"), data);

          if (res.status === 200) {
            window.location.href = route("frontend.user-profile", {
              "afterRegistration": 1
            });
          }
        }
      } catch (err) {
        if(err.response.status === 423) {
          errorMessage.value = err.response.data.error;
        }
      }
    };

    return {
      getErrorsFor,
      v$,
      delayTouch,
      onSubmit,
      form,
      positions,
      showSuccessMessage,
      errorMessage
    };
  },
};
</script>

<style>
</style>
